import { READER_WAITING_LABEL } from '@/components/reader';
import { EXPIRED_HEADING, EXPIRED_SENTENCE, INTERRUPTED_SENTENCE } from '@/lib/session-labels';
import type { ButtonStyle } from '@matter_extractor/shared';
import type { CSSProperties } from 'react';
import { SESSION_TEST_IDS } from './contracts';

/**
 * « Donner mon avis » (retour-experience.md §6.2): the one button a closing
 * panel gains when the experience has a page of retour.
 */
export interface SessionEndingFeedback {
  /** The press: the advance towards the page of retour. */
  readonly onGive: () => void;
  /** That advance is in flight: the button says so and answers nothing else. */
  readonly waiting: boolean;
  /** How the theme paints a Bouton - the panel's button is painted like the experience's. */
  readonly buttonStyle: ButtonStyle;
}

export interface SessionEndingProps {
  /** Which of the ends of §5.3 (and of #199, `interrupted`) is on screen. */
  readonly kind: 'expired' | 'closed' | 'interrupted';
  /** The engine's excuse, on `closed` only - the closing text of the step or action. */
  readonly reason: string | null;
  /** On `closed` only, and only when the experience has a page of retour (§6.2). */
  readonly feedback?: SessionEndingFeedback;
}

/** The label of the button: FIXED, never set by an operator (§6.2). */
export const GIVE_FEEDBACK_LABEL = 'Donner mon avis';

/**
 * The three ends that are NOT the last page: the two of §5.3, and the interruption.
 *
 * - **`interrompue`** (issue #199, interruption.md §6) - the sober, fixed screen
 *   of the expiration, under the SAME title, with a sentence of its own:
 *   « Elle a été arrêtée. » No button, no page of retour, no « Donner mon
 *   avis » - an operator said the session is over, and nothing here suggests it
 *   can be argued with. A tab left open falls onto it by the `status` frame.
 *
 * - **`expirée`** - « un écran de fin sobre et fixe ». Fixed is the
 *   requirement: no button, no retry, nothing that suggests the 24 hours can be
 *   argued with. « Les pages de fin ne sont pas jouées » (H2, confirmed), so
 *   the experience's own last page never appears behind this.
 * - **`fermée`** - « la phrase d'excuse du moteur, et rien d'autre : ni page
 *   suivante, ni bouton ». The sentence is the engine's own closing text
 *   (§7.2, §8.4, §8.5), frozen on the session when it closed; until the
 *   Protocoles lot writes it, nothing in this repo can produce this state, and
 *   the fallback below is what a row with no reason reads as. Since issue #176
 *   ONE exception, and it is a single button: when the experience has a page of
 *   retour the panel keeps its form - the title and the excuse - and gains
 *   « Donner mon avis » (retour-experience.md §6.2). Without a page of retour
 *   the panel is exactly what it was.
 *
 * Painted in the THEME like everything else inside the window: the person is
 * still inside the experience when it ends, and a PMS-blue panel would be the
 * one thing on the page that is not hers. The button is the reader's own
 * `lecteur-bouton`, so it takes the theme's colours, face and shape through the
 * same custom properties; it is as wide as its label and sits to the left, 24 px
 * under the excuse (the mockup `lecteur-fermeture.html`, choix de forme 7).
 */
const PANEL: CSSProperties = {
  maxWidth: 'min(36rem, 100%)',
  color: 'var(--lecteur-color-text)',
};

const TITLE: CSSProperties = {
  fontFamily: 'var(--lecteur-font-title)',
  fontSize: 'var(--lecteur-large-size)',
  lineHeight: 'var(--lecteur-large-line)',
};

const BODY: CSSProperties = {
  color: 'var(--lecteur-color-muted)',
  fontFamily: 'var(--lecteur-font-text)',
  fontSize: 'var(--lecteur-normal-size)',
  lineHeight: 'var(--lecteur-normal-line)',
};

const ENDING_TEST_ID = {
  expired: SESSION_TEST_IDS.expired,
  closed: SESSION_TEST_IDS.closed,
  interrupted: SESSION_TEST_IDS.interrupted,
} as const;

export function SessionEnding({ kind, reason, feedback }: SessionEndingProps) {
  const sober = kind !== 'closed';
  return (
    <div data-testid={ENDING_TEST_ID[kind]} style={PANEL}>
      <h1 className="m-0" style={TITLE}>
        {sober ? EXPIRED_HEADING : 'Cette conversation s’arrête ici'}
      </h1>
      <p className="mt-3 mb-0" style={BODY}>
        {kind === 'expired'
          ? EXPIRED_SENTENCE
          : kind === 'interrupted'
            ? INTERRUPTED_SENTENCE
            : (reason ?? 'Merci pour le temps que vous avez accordé à cet entretien.')}
      </p>
      {!sober && feedback !== undefined && (
        <button
          type="button"
          className="lecteur-bouton mt-6"
          data-testid={SESSION_TEST_IDS.giveFeedback}
          data-style={feedback.buttonStyle}
          data-waiting={feedback.waiting ? 'true' : undefined}
          disabled={feedback.waiting}
          onClick={feedback.onGive}
        >
          {feedback.waiting ? (
            <span className="lecteur-attente">{READER_WAITING_LABEL}</span>
          ) : (
            GIVE_FEEDBACK_LABEL
          )}
        </button>
      )}
    </div>
  );
}
